import classNames from 'classnames';
import { List } from 'immutable';
import { isReadOnlySelector } from 'modules/MedComms/selectors';
import React, { useCallback, useEffect, useState } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import ArtboardLayoutContainerList from 'components/ArtboardLayoutContainerList';
import { ModalType } from 'const';
import { activeSSI } from 'containers/Artboards/selectors';
import { layoutsInActiveSSI } from 'containers/Common/selectors';
import { orderedActiveLayoutIds } from 'containers/Layouts/selectors';
import { showModal } from 'containers/ModalManager/actions';
import { ExtendedSectionMap, SSIMap } from 'models';
import { intlGet } from 'utils/intlGet';
import { getSSISize } from 'utils/styles/getSSISize';
import SSIActions from '../SSIActions';
import styles from './styles.module.scss';
import { useSSIDragSource } from './useSSIDragSource';

type Props = {
  section: ExtendedSectionMap;
  sectionHeight: number;
  sectionWidth: number;
  dataTestId: number;
};

// SSI is an abbreviation for Select Safety Information
const SSI: React.FC<Props> = ({
  section,
  sectionHeight,
  sectionWidth,
  dataTestId,
}) => {
  const dispatch = useDispatch();
  const artboardLayoutIds = useSelector(orderedActiveLayoutIds) as List<string>;
  const layouts = useSelector(layoutsInActiveSSI);
  const ssi = useSelector(activeSSI) as SSIMap;
  const isReadOnly = useSelector(isReadOnlySelector);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const { dragRef, preview } = useSSIDragSource();

  useEffect(() => {
    preview(getEmptyImage(), {
      captureDraggingState: true,
    });
  }, [preview]);

  const showSSISourceWindow = useCallback(() => {
    dispatch(showModal(ModalType.SSI_SOURCE));
  }, [dispatch]);

  const renderHint = (): JSX.Element | null => {
    if (!isMouseOver) {
      return null;
    }

    return <div className={styles.hint}>{intlGet('SSI', 'Hint')}</div>;
  };

  const renderSSIContent = (): JSX.Element => {
    if (!layouts) {
      return (
        <div className={styles.placeholder} onDoubleClick={showSSISourceWindow}>
          {intlGet('SSI', 'Placeholder')}
        </div>
      );
    }

    return (
      <div className={styles.ssiContentWrapper} data-testid='ssiContentWrapper'>
        <div className={styles.ssiContent} data-testid='ssiContent'>
          <ArtboardLayoutContainerList
            isSectionActive={false}
            layoutIds={artboardLayoutIds}
            layouts={layouts}
            ssi={ssi}
            dataTestId={`section${dataTestId}-ssi`}
            section={section}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(styles.ssi, {
        [styles.highlighted]: isMouseOver,
      })}
      style={getSSISize(ssi, sectionWidth, sectionHeight)}
      onMouseEnter={(): void => setIsMouseOver(true)}
      onMouseLeave={(): void => setIsMouseOver(false)}
      data-testid={`section${dataTestId}-ssi`}
    >
      {renderHint()}
      {renderSSIContent()}
      <SSIActions connectDragSource={dragRef} />
    </div>
  );
};

export default SSI;
